/**
 * Reusable entitlements form (same structure as plan_license).
 * Used by AdminPlanLicenses (plan license edit) and LicenseEntitlementsModal (company license entitlements override).
 */
import React, { useMemo } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import MultiSelectDropdown from '../../components/layout/MultiSelectDropdown1';
import ObjectPermissionMatrix from '../../components/common/ObjectPermissionMatrix';
import { allowedObjectsForDisplay, isReportOrDashboardStandardTab, isAlwaysMigrateObject, SETUP_OPTIONS, STANDARD_OBJECT_OPTIONS } from './AdminPlanConfigConstants';

const EntitlementsForm = ({
  entitlementsConfig,
  onChange,
  planConfig,
  disabled,
  standardObjects = [],
  standardTabs = [],
}) => {
  const allStandardOptions = useMemo(() => {
    if (standardObjects.length > 0) {
      return standardObjects.map((o) => {
        const name = o.object_name || '';
        const label = name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return { name, label: label || name };
      });
    }
    return STANDARD_OBJECT_OPTIONS.map((o) => ({ name: o.name, label: o.label }));
  }, [standardObjects]);

  const planAllowedObjects = useMemo(() => {
    if (Array.isArray(planConfig?.allowedObjectsPermission) && planConfig.allowedObjectsPermission.length > 0) {
      return planConfig.allowedObjectsPermission;
    }
    if (Array.isArray(planConfig?.objectManager?.allowedObjects) && planConfig.objectManager.allowedObjects.length > 0) {
      return planConfig.objectManager.allowedObjects;
    }
    return [];
  }, [planConfig]);

  /** Objects to show in Object Permissions: only those that are in the license's allowed objects (Object Manager selection). */
  const objectPermissionsAllowedObjects = useMemo(() => {
    const licenseAllowed = entitlementsConfig?.objectManager?.allowedObjects ?? [];
    if (!Array.isArray(licenseAllowed) || licenseAllowed.length === 0) return [];
    const planSet = new Set(planAllowedObjects);
    return licenseAllowed.filter((name) => planSet.has(name));
  }, [planAllowedObjects, entitlementsConfig?.objectManager?.allowedObjects]);

  const objectManagerPicklistForPlan = useMemo(() => {
    if (!planAllowedObjects.length) return [];
    const allowed = new Set(planAllowedObjects);
    const base = allStandardOptions
      .filter((o) => allowed.has(o.name) && !isAlwaysMigrateObject(o.name))
      .map((o) => ({ key: o.name, value: o.label }));
    const selected = entitlementsConfig?.objectManager?.allowedObjects ?? [];
    const keySet = new Set(base.map((o) => o.key));
    const extra = selected
      .filter((k) => k && !isAlwaysMigrateObject(k) && allowed.has(k) && !keySet.has(k))
      .map((k) => ({ key: k, value: (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim() || k }));
    return extra.length > 0 ? [...base, ...extra] : base;
  }, [planAllowedObjects, allStandardOptions, entitlementsConfig?.objectManager?.allowedObjects]);

  const setupPicklistForPlan = useMemo(() => {
    if (planConfig?.setup?.showSetup === false) return [];
    if (!planConfig?.setup?.allowedItems?.length) return [];
    const allowed = new Set(planConfig.setup.allowedItems);
    return SETUP_OPTIONS.filter((o) => allowed.has(o.key));
  }, [planConfig]);

  const planLeadConversionObjects = useMemo(() => {
    if (Array.isArray(planConfig?.leadConversionMapping?.allowedObjects) && planConfig.leadConversionMapping.allowedObjects.length > 0) {
      return planConfig.leadConversionMapping.allowedObjects;
    }
    return [];
  }, [planConfig]);

  const leadConversionObjectsForPlan = useMemo(() => {
    if (!planLeadConversionObjects.length) return [];
    const allowed = new Set(planLeadConversionObjects);
    const base = allStandardOptions
      .filter((o) => allowed.has(o.name))
      .map((o) => ({ key: o.name, value: o.label }));
    const baseKeys = new Set(base.map((o) => o.key));
    // Include custom objects that are in plan allowedObjects but not part of standard options.
    const customExtras = planLeadConversionObjects
      .filter((name) => name && !baseKeys.has(name))
      .map((name) => ({
        key: name,
        value: String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      }));
    return customExtras.length > 0 ? [...base, ...customExtras] : base;
  }, [planLeadConversionObjects, allStandardOptions]);

  const planScoringRuleObjects = useMemo(() => {
    if (!Array.isArray(planConfig?.scoringRule?.allowedObjects) || planConfig.scoringRule.allowedObjects.length === 0) return [];
    const allowed = new Set(planConfig.scoringRule.allowedObjects);
    return allStandardOptions.filter((o) => allowed.has(o.name)).map((o) => ({ key: o.name, value: o.label }));
  }, [planConfig?.scoringRule?.allowedObjects, allStandardOptions]);

  const planAssignmentRuleObjects = useMemo(() => {
    if (!Array.isArray(planConfig?.assignmentRule?.allowedObjects) || planConfig.assignmentRule.allowedObjects.length === 0) return [];
    const allowed = new Set(planConfig.assignmentRule.allowedObjects);
    return allStandardOptions.filter((o) => allowed.has(o.name)).map((o) => ({ key: o.name, value: o.label }));
  }, [planConfig?.assignmentRule?.allowedObjects, allStandardOptions]);

  const planWebformObjects = useMemo(() => {
    if (!Array.isArray(planConfig?.webform?.allowedObjects) || planConfig.webform.allowedObjects.length === 0) return [];
    const allowed = new Set(planConfig.webform.allowedObjects);
    return allStandardOptions.filter((o) => allowed.has(o.name)).map((o) => ({ key: o.name, value: o.label }));
  }, [planConfig?.webform?.allowedObjects, allStandardOptions]);

  const planSearchViewObjects = useMemo(() => {
    if (!Array.isArray(planConfig?.searchView?.allowedObjects) || planConfig.searchView.allowedObjects.length === 0) return [];
    const allowed = new Set(planConfig.searchView.allowedObjects);
    return allStandardOptions.filter((o) => allowed.has(o.name)).map((o) => ({ key: o.name, value: o.label }));
  }, [planConfig?.searchView?.allowedObjects, allStandardOptions]);

  const planMax = useMemo(
    () => ({
      field: planConfig?.field?.maxCustom ?? 999,
      layout: planConfig?.layout?.maxLayouts ?? 99,
      leadConversion: planConfig?.leadConversionMapping?.maxObjects ?? 99,
      tabMaxTabs: planConfig?.tab?.maxTabs,
    }),
    [planConfig]
  );

  const standardTabPicklistOptions = useMemo(() => {
    const allowedObjectNames = planConfig?.allowedObjectsPermission ?? [];
    const enabledPackages = planConfig?.enabledPackages ?? [];
    const formatKey = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return standardTabs
      .filter((t) => {
        const tabPackage = t.config?.package && String(t.config.package).trim();
        const packageOk = !tabPackage || enabledPackages.includes(tabPackage);
        if (!packageOk) return false;
        return (
          isReportOrDashboardStandardTab(t) ||
          !t.object_name ||
          allowedObjectNames.includes(t.object_name)
        );
      })
      .map((t) => {
        const displayName = t.label || (t.name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || t.name;
        const suffixParts = [t.object_name, t.config?.package].filter(Boolean).map(formatKey);
        const value = suffixParts.length > 0 ? `${displayName} (${suffixParts.join(', ')})` : displayName;
        return { key: t.id, value };
      });
  }, [standardTabs, planConfig?.allowedObjectsPermission, planConfig?.enabledPackages]);

  /** Report/Dashboard tab IDs so they stay visible in license tab picklist even when not in Object Manager allowed objects. */
  const reportDashboardTabIds = useMemo(
    () => new Set(standardTabs.filter(isReportOrDashboardStandardTab).map((t) => t.id)),
    [standardTabs]
  );

  const planAllowedTabIds = useMemo(() => {
    const ids = Array.isArray(planConfig?.tab?.allowedTabs) ? planConfig.tab.allowedTabs : [];
    if (ids.length > 0) return new Set(ids);
    return new Set(standardTabPicklistOptions.map((o) => o.key));
  }, [planConfig?.tab?.allowedTabs, standardTabPicklistOptions]);

  /** Map tab id -> object_name so we can hide tabs when user removes that object from Object Manager. */
  const tabIdToObjectName = useMemo(() => {
    const m = {};
    (standardTabs || []).forEach((t) => {
      if (t.id && t.object_name) m[t.id] = t.object_name;
    });
    return m;
  }, [standardTabs]);

  /** Only show tabs for objects that are in the license's Object Manager allowed objects (or tabs with no object, or Report/Dashboard tabs). */
  const tabPicklistWithinPlan = useMemo(() => {
    const allowedObjectsSet = new Set(entitlementsConfig?.objectManager?.allowedObjects ?? []);
    return standardTabPicklistOptions.filter((o) => {
      if (!planAllowedTabIds.has(o.key)) return false;
      if (reportDashboardTabIds.has(o.key)) return true;
      const objName = tabIdToObjectName[o.key];
      return !objName || allowedObjectsSet.has(objName);
    });
  }, [standardTabPicklistOptions, planAllowedTabIds, reportDashboardTabIds, entitlementsConfig?.objectManager?.allowedObjects, tabIdToObjectName]);

  const tabAllowedTabsSelected = useMemo(() => {
    const saved = entitlementsConfig.tab?.allowedTabs ?? [];
    if (Array.isArray(saved) && saved.length > 0) {
      const validIds = new Set(tabPicklistWithinPlan.map((o) => o.key));
      return saved.filter((id) => validIds.has(id));
    }
    return tabPicklistWithinPlan.map((o) => o.key);
  }, [entitlementsConfig.tab?.allowedTabs, tabPicklistWithinPlan]);

  if (!planConfig) return null;

  return (
    <>
      <Row className="g-3">
        <Col xs={12}>
          <Card className="p-3">
            <h6 className="mb-2">Setup</h6>
            <Form.Check
              type="switch"
              size="sm"
              label="Show setup"
              checked={entitlementsConfig.setup?.showSetup !== false}
              onChange={(e) => onChange((prev) => ({ ...prev, setup: { ...prev.setup, showSetup: e.target.checked } }))}
              disabled={disabled}
              className="mb-2"
            />
            <Form.Text className="d-block mb-0 text-muted small">
              When enabled, configure Setup Menu below (which items appear in Setup). Then Field, Layout, Workflow (if allowed), Scoring Rule, Assignment Rule, Web Forms, Search Views, Lead Conversion and Migration, and Object Manager. Tab, Object Manager allowed objects, Report, Dashboard, and Object Permissions are common permissions (not part of Setup).
            </Form.Text>
          </Card>
        </Col>
      </Row>

      {entitlementsConfig.setup?.showSetup !== false && setupPicklistForPlan.length > 0 && (
        <Row className="g-3">
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Setup Menu</h6>
              <Form.Group className="mb-2">
                <Form.Label className="small">Mode</Form.Label>
                <Form.Select
                  size="sm"
                  value={entitlementsConfig.setup?.mode ?? 'allowlist'}
                  onChange={(e) => onChange((prev) => ({ ...prev, setup: { ...prev.setup, mode: e.target.value } }))}
                  disabled={disabled}
                >
                  <option value="allowlist">allowlist</option>
                  <option value="denylist">denylist</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small">Allowed setup items</Form.Label>
                <MultiSelectDropdown
                  selectedValues={entitlementsConfig.setup?.allowedItems ?? []}
                  picklistValues={setupPicklistForPlan}
                  onChange={(values) => onChange((prev) => ({ ...prev, setup: { ...prev.setup, allowedItems: values } }))}
                  placeholder="Items (plan allowed)"
                  disabled={disabled}
                />
              </Form.Group>
              <Form.Text className="text-muted small d-block">Which setup menu items are available. If Workflow Manager is not selected, the Workflow section below is hidden.</Form.Text>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="g-3">
        <Col xs={12}>
          <Card className="p-3">
            <h6 className="mb-2">Allowed Standard Tabs</h6>
            <Form.Text className="d-block mb-2 text-muted small">
              This is a common permission. Only tabs for objects selected in Object Manager are shown. Removing an object there hides its tab here.
            </Form.Text>
            <Form.Group className="mb-3">
              <Form.Label className="small">Max custom tabs</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                min={0}
                max={
                  planMax.tabMaxTabs === -1 || planMax.tabMaxTabs == null || !Number.isFinite(Number(planMax.tabMaxTabs))
                    ? undefined
                    : Number(planMax.tabMaxTabs)
                }
                value={entitlementsConfig.tab?.maxTabs ?? ''}
                onChange={(e) => {
                  const raw = Math.max(0, Number(e.target.value) || 0);
                  const cap = planMax.tabMaxTabs;
                  const v =
                    cap === -1 || cap == null || !Number.isFinite(Number(cap))
                      ? raw
                      : Math.min(raw, Number(cap));
                  onChange((prev) => ({ ...prev, tab: { ...prev.tab, maxTabs: v } }));
                }}
                disabled={disabled}
              />
              {planConfig?.tab?.maxTabs != null && (
                <Form.Text className="text-muted small">
                  Plan max: {planConfig.tab.maxTabs === -1 ? 'unlimited' : planConfig.tab.maxTabs}
                </Form.Text>
              )}
            </Form.Group>
            <Form.Check
              type="switch"
              size="sm"
              label="Allow create tab"
              className="mb-3"
              checked={entitlementsConfig.tab?.allowCreate !== false}
              onChange={(e) =>
                onChange((prev) => ({ ...prev, tab: { ...prev.tab, allowCreate: e.target.checked } }))
              }
              disabled={disabled || planConfig?.tab?.allowCreate === false}
            />
            {tabPicklistWithinPlan.length > 0 ? (
              <Form.Group className="mb-0">
                <Form.Label className="small">Allowed Standard Tabs</Form.Label>
                <MultiSelectDropdown
                  picklistValues={tabPicklistWithinPlan}
                  selectedValues={tabAllowedTabsSelected}
                  onChange={(values) => onChange((prev) => ({ ...prev, tab: { ...prev.tab, allowedTabs: values } }))}
                  disabled={disabled}
                  placeholder="Select tabs..."
                />
                <Form.Text className="text-muted small">Shows object and package when set.</Form.Text>
              </Form.Group>
            ) : (
              <Form.Text className="text-muted small">Select objects in Object Manager first; then their tabs appear here.</Form.Text>
            )}
          </Card>
        </Col>
      </Row>

      {entitlementsConfig.setup?.showSetup !== false && (
        <Row className="g-3">
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Field</h6>
              <Form.Group className="mb-2">
                <Form.Label className="small">Max Custom</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder="Max Custom"
                  min={0}
                  max={planMax.field}
                  value={entitlementsConfig.field?.maxCustom ?? ''}
                  onChange={(e) => {
                    const v = Math.min(Number(e.target.value) || 0, planMax.field);
                    onChange((prev) => ({ ...prev, field: { ...prev.field, maxCustom: v } }));
                  }}
                />
                {planConfig?.field?.maxCustom != null && <Form.Text className="text-muted small">Plan max: {planConfig.field.maxCustom}</Form.Text>}
              </Form.Group>
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Create"
                checked={!!entitlementsConfig.field?.allowCreate}
                onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowCreate: e.target.checked } }))}
                disabled={disabled || !planConfig?.field?.allowCreate}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Standard Edit"
                checked={!!entitlementsConfig.field?.allowStandardEdit}
                onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowStandardEdit: e.target.checked } }))}
                disabled={disabled || !planConfig?.field?.allowStandardEdit}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Standard Delete"
                checked={!!entitlementsConfig.field?.allowStandardDelete}
                onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowStandardDelete: e.target.checked } }))}
                disabled={disabled || !planConfig?.field?.allowStandardDelete}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Edit Label"
                checked={!!entitlementsConfig.field?.allowEditLabel}
                onChange={(e) => onChange((prev) => ({ ...prev, field: { ...prev.field, allowEditLabel: e.target.checked } }))}
                disabled={disabled || !planConfig?.field?.allowEditLabel}
              />
            </Card>
          </Col>
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Layout</h6>
              <Form.Group className="mb-2">
                <Form.Label className="small">Count Mode</Form.Label>
                <Form.Select
                size="sm"
                value={entitlementsConfig.layout?.countMode ?? 'pair'}
                onChange={(e) => onChange((prev) => ({ ...prev, layout: { ...prev.layout, countMode: e.target.value } }))}
                disabled={disabled}
              >
                <option value="pair">pair</option>
                <option value="single">single</option>
              </Form.Select>
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label className="small">Max Layouts</Form.Label>
                <Form.Control
                  type="number"
                  size="sm"
                  placeholder="Max Layouts"
                  min={planMax.layout === -1 ? -1 : 0}
                  max={planMax.layout === -1 ? undefined : planMax.layout}
                  value={entitlementsConfig.layout?.maxLayouts ?? ''}
                  onChange={(e) => {
                    const raw = Number(e.target.value);
                    let v;
                    if (Number.isNaN(raw)) {
                      v = 0;
                    } else if (raw === -1) {
                      v = -1;
                    } else if (raw < 0) {
                      v = 0;
                    } else if (planMax.layout === -1) {
                      v = raw;
                    } else {
                      v = Math.min(raw, planMax.layout);
                    }
                    onChange((prev) => ({ ...prev, layout: { ...prev.layout, maxLayouts: v } }));
                  }}
                />
                {planConfig?.layout?.maxLayouts != null && <Form.Text className="text-muted small">Plan max: {planConfig.layout.maxLayouts}</Form.Text>}
              </Form.Group>
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Create"
                checked={!!entitlementsConfig.layout?.allowCreate}
                onChange={(e) => onChange((prev) => ({ ...prev, layout: { ...prev.layout, allowCreate: e.target.checked } }))}
                disabled={disabled || !planConfig?.layout?.allowCreate}
              />
            </Card>
          </Col>
          {(entitlementsConfig.setup?.allowedItems ?? []).map((i) => String(i).toLowerCase()).includes('workflows') && (
            <Col md={6}>
              <Card className="p-3">
                <h6 className="mb-2">Workflow</h6>
                <Form.Text className="text-muted small d-block mb-2">Workflow Manager in Setup Menu gives view access. Limit (max count) is set at plan/subscription; license controls create only (create = edit/delete).</Form.Text>
                <Form.Check
                  type="switch"
                  size="sm"
                  label="Allow Create Workflow"
                  checked={!!entitlementsConfig.workflow?.allowCreateWorkflow}
                  onChange={(e) =>
                    onChange((prev) => ({
                      ...prev,
                      workflow: { ...prev.workflow, allowCreateWorkflow: e.target.checked },
                    }))
                  }
                  disabled={disabled || !planConfig?.workflow?.allowCreateWorkflow}
                />
              </Card>
            </Col>
          )}
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Scoring Rules</h6>
              <Form.Check
                type="switch"
                size="sm"
                label="Allow View Scoring Rules"
                checked={!!entitlementsConfig.scoringRule?.allowViewScoringRule}
                onChange={(e) => onChange((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowViewScoringRule: e.target.checked } }))}
                disabled={disabled || !planConfig?.scoringRule?.allowViewScoringRule}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Create / Edit Scoring Rules"
                checked={!!entitlementsConfig.scoringRule?.allowCreateScoringRule}
                onChange={(e) => onChange((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowCreateScoringRule: e.target.checked } }))}
                disabled={disabled || !planConfig?.scoringRule?.allowCreateScoringRule}
              />
              {planScoringRuleObjects.length > 0 && (
                <Form.Group className="mb-0 mt-2">
                  <Form.Label className="small">Objects allowed for scoring rules</Form.Label>
                  <MultiSelectDropdown
                    picklistValues={planScoringRuleObjects}
                    selectedValues={entitlementsConfig.scoringRule?.allowedObjects ?? []}
                    onChange={(values) => onChange((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowedObjects: values } }))}
                    placeholder="Select objects (plan allowed)"
                    disabled={disabled}
                  />
                </Form.Group>
              )}
            </Card>
          </Col>
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Assignment Rules</h6>
              <Form.Check
                type="switch"
                size="sm"
                label="Allow View Assignment Rules"
                checked={!!entitlementsConfig.assignmentRule?.allowViewAssignmentRule}
                onChange={(e) => onChange((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowViewAssignmentRule: e.target.checked } }))}
                disabled={disabled || !planConfig?.assignmentRule?.allowViewAssignmentRule}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Create / Edit Assignment Rules"
                checked={!!entitlementsConfig.assignmentRule?.allowCreateAssignmentRule}
                onChange={(e) => onChange((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowCreateAssignmentRule: e.target.checked } }))}
                disabled={disabled || !planConfig?.assignmentRule?.allowCreateAssignmentRule}
              />
              {planAssignmentRuleObjects.length > 0 && (
                <Form.Group className="mb-0 mt-2">
                  <Form.Label className="small">Objects allowed for assignment rules</Form.Label>
                  <MultiSelectDropdown
                    picklistValues={planAssignmentRuleObjects}
                    selectedValues={entitlementsConfig.assignmentRule?.allowedObjects ?? []}
                    onChange={(values) => onChange((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowedObjects: values } }))}
                    placeholder="Select objects (plan allowed)"
                    disabled={disabled}
                  />
                </Form.Group>
              )}
            </Card>
          </Col>
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Web Forms</h6>
              <Form.Check
                type="switch"
                size="sm"
                label="Allow View Web Forms"
                checked={!!entitlementsConfig.webform?.allowViewWebform}
                onChange={(e) => onChange((prev) => ({ ...prev, webform: { ...prev.webform, allowViewWebform: e.target.checked } }))}
                disabled={disabled || !planConfig?.webform?.allowViewWebform}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Create / Edit Web Forms"
                checked={!!entitlementsConfig.webform?.allowCreateWebform}
                onChange={(e) => onChange((prev) => ({ ...prev, webform: { ...prev.webform, allowCreateWebform: e.target.checked } }))}
                disabled={disabled || !planConfig?.webform?.allowCreateWebform}
              />
              {planWebformObjects.length > 0 && (
                <Form.Group className="mb-0 mt-2">
                  <Form.Label className="small">Objects allowed for web forms</Form.Label>
                  <MultiSelectDropdown
                    picklistValues={planWebformObjects}
                    selectedValues={entitlementsConfig.webform?.allowedObjects ?? []}
                    onChange={(values) => onChange((prev) => ({ ...prev, webform: { ...prev.webform, allowedObjects: values } }))}
                    placeholder="Select objects (plan allowed)"
                    disabled={disabled}
                  />
                </Form.Group>
              )}
            </Card>
          </Col>
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Search Views</h6>
              <Form.Check
                type="switch"
                size="sm"
                label="Allow View Search Views"
                checked={!!entitlementsConfig.searchView?.allowViewSearchView}
                onChange={(e) => onChange((prev) => ({ ...prev, searchView: { ...prev.searchView, allowViewSearchView: e.target.checked } }))}
                disabled={disabled || !planConfig?.searchView?.allowViewSearchView}
              />
              <Form.Check
                type="switch"
                size="sm"
                label="Allow Create / Edit Search Views"
                checked={!!entitlementsConfig.searchView?.allowCreateSearchView}
                onChange={(e) => onChange((prev) => ({ ...prev, searchView: { ...prev.searchView, allowCreateSearchView: e.target.checked } }))}
                disabled={disabled || !planConfig?.searchView?.allowCreateSearchView}
              />
              {planSearchViewObjects.length > 0 && (
                <Form.Group className="mb-0 mt-2">
                  <Form.Label className="small">Objects allowed for search views</Form.Label>
                  <MultiSelectDropdown
                    picklistValues={planSearchViewObjects}
                    selectedValues={entitlementsConfig.searchView?.allowedObjects ?? []}
                    onChange={(values) => onChange((prev) => ({ ...prev, searchView: { ...prev.searchView, allowedObjects: values } }))}
                    placeholder="Select objects (plan allowed)"
                    disabled={disabled}
                  />
                </Form.Group>
              )}
            </Card>
          </Col>
          <Col md={6}>
            <Card className="p-3">
              <h6 className="mb-2">Lead Conversion and Migration</h6>
              {planConfig?.migration?.allow ? (
                <Form.Check
                  type="switch"
                  size="sm"
                  label="Data Migration"
                  checked={!!entitlementsConfig.migration?.allow}
                  onChange={(e) => onChange((prev) => ({ ...prev, migration: { ...prev.migration, allow: e.target.checked } }))}
                  disabled={disabled}
                  className="mb-2"
                />
              ) : null}
              {planConfig?.leadConversionMapping?.allow ? (
                <>
                  <Form.Check
                    type="switch"
                    size="sm"
                    label="Allow"
                    checked={!!entitlementsConfig.leadConversionMapping?.allow}
                    onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allow: e.target.checked } }))}
                    disabled={disabled}
                  />
                  <Form.Check
                    type="switch"
                    size="sm"
                    label="Allow Custom Objects"
                    checked={!!entitlementsConfig.leadConversionMapping?.allowCustomObject}
                    onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allowCustomObject: e.target.checked } }))}
                    disabled={disabled || planConfig?.leadConversionMapping?.allowCustomObject === false}
                  />
                  <Form.Select
                    size="sm"
                    value={entitlementsConfig.leadConversionMapping?.mode ?? 'allowlist'}
                    onChange={(e) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, mode: e.target.value } }))}
                    disabled={disabled}
                  >
                    <option value="allowlist">allowlist</option>
                    <option value="denylist">denylist</option>
                  </Form.Select>
                  <Form.Control
                    type="number"
                    size="sm"
                    className="mt-1"
                    placeholder="Max Objects"
                    min={0}
                    max={planMax.leadConversion}
                    value={entitlementsConfig.leadConversionMapping?.maxObjects ?? ''}
                    onChange={(e) => {
                      const v = Math.min(Number(e.target.value) || 0, planMax.leadConversion);
                      onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, maxObjects: v } }));
                    }}
                  />
                  {planConfig?.leadConversionMapping?.maxObjects != null && (
                    <Form.Text className="text-muted small">Plan max: {planConfig.leadConversionMapping.maxObjects}</Form.Text>
                  )}
                  <MultiSelectDropdown
                    selectedValues={entitlementsConfig.leadConversionMapping?.allowedObjects ?? []}
                    picklistValues={leadConversionObjectsForPlan}
                    onChange={(values) => onChange((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: values } }))}
                    placeholder="Objects (plan allowed)"
                    disabled={disabled}
                  />
                </>
              ) : !planConfig?.migration?.allow ? (
                <Form.Text className="text-muted small">Not included in plan</Form.Text>
              ) : null}
            </Card>
          </Col>
        </Row>
      )}

      <Row className="g-3">
        <Col xs={12}>
          <Card className="p-3">
            <h6 className="mb-2">Object Manager</h6>
            <Form.Text className="d-block mb-2 text-muted small">
              Allowed objects is a common permission. Tabs for removed objects are automatically hidden from Allowed Standard Tabs. Allow Create and Enable Standard are part of Setup.
            </Form.Text>
            {objectManagerPicklistForPlan.length > 0 ? (
              <>
                {entitlementsConfig.setup?.showSetup !== false && (
                  <>
                    <Form.Check
                      type="switch"
                      size="sm"
                      label="Allow Create"
                      checked={!!entitlementsConfig.objectManager?.allow_create}
                      onChange={(e) => onChange((prev) => ({ ...prev, objectManager: { ...prev.objectManager, allow_create: e.target.checked } }))}
                      disabled={disabled || !planConfig?.objectManager?.allow_create}
                      className="mb-2"
                    />
                    <Form.Check
                      type="switch"
                      size="sm"
                      label="Enable Standard"
                      checked={!!entitlementsConfig.objectManager?.enable_standard}
                      onChange={(e) => onChange((prev) => ({ ...prev, objectManager: { ...prev.objectManager, enable_standard: e.target.checked } }))}
                      disabled={disabled || !planConfig?.objectManager?.enable_standard}
                      className="mb-2"
                    />
                  </>
                )}
                <Form.Group className="mb-0">
                  <Form.Label className="small">Allowed Objects</Form.Label>
                  <MultiSelectDropdown
                    selectedValues={allowedObjectsForDisplay(entitlementsConfig.objectManager?.allowedObjects ?? [])}
                    picklistValues={objectManagerPicklistForPlan}
                    onChange={(values) => {
                      const allowedSet = new Set(values);
                      const currentTabs = entitlementsConfig.tab?.allowedTabs ?? [];
                      const newAllowedTabs = currentTabs.filter((tabId) => {
                        const obj = tabIdToObjectName[tabId];
                        return !obj || allowedSet.has(obj);
                      });
                      onChange((prev) => ({
                        ...prev,
                        objectManager: { ...prev.objectManager, allowedObjects: values },
                        tab: { ...prev.tab, allowedTabs: newAllowedTabs },
                      }));
                    }}
                    placeholder="Objects (plan allowed)"
                    disabled={disabled}
                  />
                  <Form.Text className="text-muted small d-block mt-1">
                    Activity, Task, Note, Email log, Product, Product price, User, Role, and Password reset are always included in migration and are not shown here.
                  </Form.Text>
                </Form.Group>
              </>
            ) : (
              <Form.Text className="text-muted small">None allowed by plan.</Form.Text>
            )}
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12}>
          <Card className="p-3">
            <h6 className="mb-2">Object Permissions</h6>
            <Form.Text className="d-block mb-2 text-muted small">
              Permissions are shown only for objects selected in Object Manager (Allowed Objects) above. Restrict which object permissions this license can grant to profiles; admin cannot grant permissions beyond what the plan allows.
            </Form.Text>
            <ObjectPermissionMatrix
              objects={objectPermissionsAllowedObjects.map((name) => {
                const opt = allStandardOptions.find((o) => String(o.name).toLowerCase() === String(name).toLowerCase());
                const displayLabel = opt?.label || (String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || name);
                return { object_name: name, object_display_name: displayLabel };
              })}
              permissions={entitlementsConfig.objectPermissions ?? {}}
              onChange={(objectPermissions) => onChange((prev) => ({ ...prev, objectPermissions }))}
              disabled={disabled}
              ceilingPermissions={planConfig?.objectPermissions}
              showSearch={true}
            />
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md={6}>
          <Card className="p-3">
            <h6 className="mb-2">Report</h6>
            <Form.Check
              type="switch"
              size="sm"
              label="Allow Create Report"
              checked={!!entitlementsConfig.report?.allowCreateReport}
              onChange={(e) => onChange((prev) => ({ ...prev, report: { ...prev.report, allowCreateReport: e.target.checked } }))}
              disabled={disabled || !planConfig?.report?.allowCreateReport}
            />
          </Card>
        </Col>
        <Col md={6}>
          <Card className="p-3">
            <h6 className="mb-2">Dashboard</h6>
            <Form.Check
              type="switch"
              size="sm"
              label="Allow Create Dashboard"
              checked={!!entitlementsConfig.dashboard?.allowCreateDashboard}
              onChange={(e) => onChange((prev) => ({ ...prev, dashboard: { ...prev.dashboard, allowCreateDashboard: e.target.checked } }))}
              disabled={disabled || !planConfig?.dashboard?.allowCreateDashboard}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default EntitlementsForm;
